import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PropertyListEditor from 'crm_components/dialog/propertyListEditor/PropertyListEditor';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import { trackClickEditDefaultColumnsLink, trackDealTableEditColumnsSave } from '../../utils/usageTracker';
import { DEAL } from 'customer-data-objects/constants/ObjectTypes';
import { COLUMN_SETTINGS } from 'customer-data-objects/property/PropertyListTypes';
import { computeLinkToEditDefaultColumns } from '../../constants/Links';
import { getIsUngatedToForecastingMultipleOutcomes, getHasDealTagsAccess, getScopes, getHasSuperAdminScope } from '../../selectors/authSelectors';
import { getProperties, getPropertyGroups, getPropertyGroupsStatus } from '../../selectors/properties';
import { getIsDealStageView, getIsWeightedOrTotalPortalConfig, getRepDealColumnsForSelectedForecastType, getRepDealColumnsStatus, getSelectedForecastType, getSelectedForecastTypeIdOrGatedDefault, getUserHasAccessToMoreThanDefaultForecastType } from '../../selectors/forecastSelectors';
import { logRavenException } from 'ui-deal-management-components-lib/utils/ravenWrapperUtil';
import { getPropertyGroupsAsOptions, getSelectedColumns } from '../../utils/properties/dealTableColumns';
import { haveSelectedColumnsBeenUpdated } from '../../utils/deals';
import { SUCCEEDED } from '../../constants/RequestStatusTypes';
import { deleteRepDealColumns, updateRepDealColumns } from '../../actions/ForecastActions';
import { fetchDefaultRepDealColumns } from '../../api/forecastApi';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EditColumnsModal = ({
  onClose
}) => {
  const dispatch = useDispatch();
  const isFmoEnabled = useSelector(getIsUngatedToForecastingMultipleOutcomes);
  const properties = useSelector(getProperties);
  const propertyGroups = useSelector(getPropertyGroups);
  const propertyGroupsLoading = useSelector(getPropertyGroupsStatus) !== SUCCEEDED;
  const selectedForecastType = useSelector(getSelectedForecastType);
  const contextForecastTypeId = useSelector(getSelectedForecastTypeIdOrGatedDefault);
  const multipleForecastTypes = useSelector(getUserHasAccessToMoreThanDefaultForecastType);
  const columns = useSelector(getRepDealColumnsForSelectedForecastType);
  const repDealColumnsLoading = useSelector(getRepDealColumnsStatus) !== SUCCEEDED;
  const scopes = useSelector(getScopes);
  const isDealStageView = useSelector(getIsDealStageView);
  const isWeightedOrTotalPortalConfig = useSelector(getIsWeightedOrTotalPortalConfig);
  const isSuperAdmin = useSelector(getHasSuperAdminScope);
  const [selectedColumns, setSelectedColumns] = useState(null);
  const [unsavedColumnsAreDefault, setUnsavedColumnsAreDefault] = useState(false);
  const hasDealTagsAccess = useSelector(getHasDealTagsAccess);
  const handleUpdateSelectedColumns = useCallback(newSelectedColumns => {
    setSelectedColumns(newSelectedColumns);
    setUnsavedColumnsAreDefault(false);
  }, []);
  const handleRestoreDefaultColumns = useCallback(() => {
    fetchDefaultRepDealColumns(contextForecastTypeId).then(defaultRepDealColumns => {
      setSelectedColumns(getSelectedColumns(defaultRepDealColumns.dealColumns, properties, selectedForecastType));
      setUnsavedColumnsAreDefault(true);
    }).catch(error => {
      logRavenException(error, 'Fetch default deal table columns');
    });
  }, [properties, selectedForecastType, contextForecastTypeId]);
  const handleApplySelectedColumns = useCallback(() => {
    if (selectedColumns) {
      const selectedColumnIds = selectedColumns.map(selectedColumn => selectedColumn.value);
      if (unsavedColumnsAreDefault) {
        dispatch(deleteRepDealColumns(selectedColumnIds));
      } else {
        dispatch(updateRepDealColumns(selectedColumnIds));
      }
      trackDealTableEditColumnsSave(isDealStageView);
    }
    onClose();
  }, [dispatch, isDealStageView, onClose, selectedColumns, unsavedColumnsAreDefault]);
  if (!properties || !propertyGroups || !columns) {
    return null;
  }
  const isCreateDisabled = scopes.includes('crm-secondary-permissions') && !scopes.includes('crm-property-settings');
  const propertyCategories = getPropertyGroupsAsOptions(propertyGroups, isWeightedOrTotalPortalConfig, hasDealTagsAccess);
  const isSaveColumnsDisabled = !haveSelectedColumnsBeenUpdated(selectedColumns, columns);
  if (repDealColumnsLoading || propertyGroupsLoading) {
    return null;
  }
  return /*#__PURE__*/_jsxs(UIModal, {
    width: 1000,
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        color: "text-primary-on-fill-default",
        children: isFmoEnabled && multipleForecastTypes ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "configureColumns.editColumnsModal.titleWithForecast",
          options: {
            forecastName: selectedForecastType === null || selectedForecastType === void 0 ? void 0 : selectedForecastType.forecastName
          }
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "configureColumns.editColumnsModal.title"
        })
      })]
    }), /*#__PURE__*/_jsx(UIDialogBody, {
      children: /*#__PURE__*/_jsx(PropertyListEditor, {
        "data-selenium-test": "edit-columns-modal",
        "data-test-id": "edit-columns-modal",
        propertyCategories: propertyCategories,
        type: DEAL,
        use: COLUMN_SETTINGS,
        value: selectedColumns || getSelectedColumns(columns, properties, selectedForecastType),
        isCreateDisabled: isCreateDisabled,
        onChange: handleUpdateSelectedColumns,
        onRequiredChange: handleUpdateSelectedColumns
      })
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UIButton, {
        "data-selenium-test": "configure-columns-save-button",
        "data-test-id": "configure-columns-save-button",
        disabled: isSaveColumnsDisabled,
        use: "primary",
        onClick: handleApplySelectedColumns,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "configureColumns.editColumnsModal.actions.apply"
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        "data-selenium-test": "configure-columns-cancel-button",
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "configureColumns.editColumnsModal.actions.cancel"
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "200",
        children: [isSuperAdmin && /*#__PURE__*/_jsx(Text, {
          as: "small",
          children: /*#__PURE__*/_jsx(UIButton, {
            href: computeLinkToEditDefaultColumns(isFmoEnabled, selectedForecastType === null || selectedForecastType === void 0 ? void 0 : selectedForecastType.forecastTypeId),
            onClick: trackClickEditDefaultColumnsLink,
            use: "link",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "configureColumns.editColumnsModal.actions.configureDefaultsLink"
            })
          })
        }), /*#__PURE__*/_jsx(Text, {
          as: "small",
          children: /*#__PURE__*/_jsx(UIButton, {
            "data-selenium-test": "restore-default-columns",
            onClick: handleRestoreDefaultColumns,
            use: "link",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "configureColumns.editColumnsModal.actions.restoreDefaultColumns"
            })
          })
        })]
      })]
    })]
  });
};
export default EditColumnsModal;